import type {
	AgendaTipoValue,
	FunnelStage,
	MessageDirection,
	PhoneKind,
	TimelineKind,
	TimelineSource,
	UserRole,
} from "@/api/db/connection";
import type { SaveResult } from "@/api/db/person-write";
import type { TimelineEntry } from "@/api/db/timeline";
import type { FileFilter } from "@/api/router/cockpit/schemas";
import type { SendBlockReason, SendResult, TeaserResult } from "@/types/draft";
import { agendaTipoLabel } from "@/lib/labels/agenda";
import { finBrl, finTimelineLabel } from "@/lib/labels/financeiro";
import { milestoneTypeLabel } from "@/lib/labels/medico";

const funnelStageLabel: Record<FunnelStage, string> = {
	interest: "interesse",
	appointment: "agendamento",
	consulted: "consultou",
	quoted: "orçado",
	closed: "fechado",
	post_op: "pós-operatório",
	recurring: "recorrente",
	reactivation: "reativação",
	lost: "perdida",
	not_applicable: "não se aplica",
};

export const HojeLabels = {
	funnelStage: funnelStageLabel,
};

export const FunnelLabels = {
	stage(stage: string) {
		return (funnelStageLabel as Record<string, string | undefined>)[stage] ?? "desconhecido";
	},
};

const sexLabel: Record<string, string> = {
	F: "Feminino",
	M: "Masculino",
	O: "Outro",
};

const completenessFieldLabel: Record<string, string> = {
	cpf: "CPF",
	rg: "RG",
	birth_date: "Nascimento",
	mother_name: "Nome da mãe",
	marital_status: "Estado civil",
	occupation: "Ocupação",
	insurance: "Convênio",
	how_found: "Como conheceu",
	referred_by: "Quem indicou",
	photo_url: "Foto",
};

export const DashboardLabels = {
	error: "Não foi possível carregar os dados do painel.",
	sections: {
		commercial: "Leitura comercial",
		growth: "Crescimento",
		profile: "Perfil dos pacientes",
	},
	stat: {
		patients: "Pacientes",
		potentialLeads: "Leads potenciais",
		activeContacts: "Conversas ativas no mês",
		response: "Resposta mediana",
	},
	heatmap: {
		title: "Movimento no WhatsApp",
		messages: "mensagens desde",
		days: ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"],
	},
	funnel: {
		title: "Etapa das conversas",
		stage: funnelStageLabel,
	},
	temperature: {
		title: "Temperatura da base",
		level: { hot: "Quente", warm: "Morna", cold: "Fria" },
	},
	finance: {
		title: "Entradas e saídas",
		in: "Entradas",
		out: "Saídas",
	},
	recency: {
		title: "Último contato",
		band: {
			"7d": "essa semana",
			"30d": "esse mês",
			"90d": "até 3 meses",
			"180d": "até 6 meses",
			"365d": "até 1 ano",
			"365d+": "mais de 1 ano",
		},
	},
	birthdays: {
		title: "Aniversariantes do mês",
		none: "Ninguém faz aniversário esse mês.",
	},
	ai: {
		title: "IA da casa",
		triages: "triagens",
		errors: "erros",
	},
	map: {
		title: "Distribuição geográfica",
		ranking: "Top 10 cidades",
	},
	charts: {
		ageBands: "Faixas etárias",
		registrations: "Cadastros por mês",
		activeByMonth: "Conversas ativas por mês",
		howFound: "Como conheceu",
		insurance: "Convênios",
		referrers: "Quem mais indicou",
		completeness: "Completude por campo",
		others: "Outros",
	},
	underHour(pct: number) {
		return `${pct}% respondidas em até 1h`;
	},
	averageAge(average: number, min: number, max: number) {
		return `média ${average} anos, de ${min} a ${max}`;
	},
	field(name: string) {
		return completenessFieldLabel[name] ?? name;
	},
	month(value: string) {
		const months = [
			"jan",
			"fev",
			"mar",
			"abr",
			"mai",
			"jun",
			"jul",
			"ago",
			"set",
			"out",
			"nov",
			"dez",
		];
		const [year, month] = value.split("-");
		const index = Number(month) - 1;

		return `${months[index] ?? month}/${year.slice(2)}`;
	},
};

type SendOutcome = SendResult["outcome"];
type TeaserOutcome = TeaserResult["outcome"];

const sendOutcomeLabel: Record<SendOutcome, string> = {
	pending: "Envio registrado. Acompanhe a confirmação na conversa.",
	sent: "Mensagem enviada.",
	simulated: "Envio simulado.",
	blocked: "Bloqueada.",
};

const sendBlockReasonLabel: Record<SendBlockReason, string> = {
	marketing_lead: "Lead sem consentimento de marketing fora da janela.",
	marketing_no_optin: "Esta paciente não consente WhatsApp marketing.",
	wrong_channel: "Texto livre indisponível fora da janela de 24h.",
	target_mismatch: "Identidade do envio divergente do cadastro atual.",
	no_channel: "Pessoa sem WhatsApp vinculado.",
	window_lookup_failed: "Janela de 24h indisponível. Mensagem não enviada.",
};

const teaserOutcomeLabel: Record<TeaserOutcome, string> = {
	sent: "Abridor enviado. Mensagem de continuidade pronta.",
	simulated: "Envio do abridor simulado.",
	opted_out: "Paciente sem consentimento de marketing. Abridor não enviado.",
	pending: "Solicitação registrada. A confirmação ou resposta ainda está pendente.",
	blocked: "Abridor não enviado.",
};

const teaserBlockReasonLabel: Record<NonNullable<TeaserResult["block_reason"]>, string> = {
	no_channel: "Pessoa sem WhatsApp vinculado. Abridor não enviado.",
	in_flight: "Envio do abridor em andamento.",
	variables_not_set: "Não foi possível preparar o abridor no WhatsApp.",
	flow_failed: "O serviço de WhatsApp recusou o envio do abridor.",
};

export const SendLabels = {
	outcome: sendOutcomeLabel,
	blockReason: sendBlockReasonLabel,
	teaserOutcome: teaserOutcomeLabel,
	teaserBlockReason: teaserBlockReasonLabel,
	repeat: "Tentativa já registrada. Nenhum novo envio.",
	sendConfirm: "Enviar esta mensagem?",
	sending: "enviando mensagem...",
	teaserConfirm: "Enviar abridor?",
	teaserSending: "enviando abridor...",
};

type SaveOutcome = SaveResult["outcome"];
type SaveFacet = SaveResult["facet"];

const outcomeLabel: Record<SaveOutcome, string> = {
	linked: "Cadastro existente atualizado.",
	suggested: "Cadastro salvo com possível duplicidade.",
	none: "Cadastro salvo.",
};

const facetLabel: Record<SaveFacet, string> = {
	patient: "paciente",
	lead: "lead",
};

const cleaningFlagLabel = {
	birth_suspect: "Data de nascimento improvável.",
	suspected_non_patient: "Possível contato em nome de outra pessoa.",
	invalid_cpf: "CPF com dígito verificador inválido.",
	invalid_cep: "CEP inválido.",
	invalid_email: "Email inválido.",
};

type CleaningFlag = keyof typeof cleaningFlagLabel;

export const CadastroLabels = {
	sectionTitle: {
		identity: "Identidade",
		contact: "Contato",
		clinic: "Clínica e origem",
		consents: "Consentimentos",
	},
	field: {
		name: "Nome completo",
		cpf: "CPF",
		rg: "RG",
		birth_date: "Nascimento",
		sex: "Sexo",
		mother_name: "Nome da mãe",
		marital_status: "Estado civil",
		occupation: "Ocupação",
		insurance: "Convênio",
		preferred_channel: "Canal preferido",
		how_found: "Como conheceu",
		how_found_detail: "Detalhe",
		referred_by: "Quem indicou",
		phones: "Telefones",
		emails: "Emails",
		address: "Endereço",
		cep: "CEP",
	},
	consent: {
		sms_marketing: "SMS marketing",
		wa_marketing: "WhatsApp marketing",
		wa_reminder: "WhatsApp lembrete",
		wa_birthday: "WhatsApp aniversário",
		email_marketing: "Email marketing",
		email_reminder: "Email lembrete",
		email_birthday: "Email aniversário",
	},
	triState: {
		yes: "Sim",
		no: "Não",
		unknown: "-",
	},
	insurance_presence: "Possui convênio",
	outcome: outcomeLabel,
	facet: facetLabel,
	cleaningFlag: cleaningFlagLabel,

	savedFlags(result: SaveResult) {
		const flags: CleaningFlag[] = [];

		if (result.birth_suspect) {
			flags.push("birth_suspect");
		}

		if (result.suspected_non_patient) {
			flags.push("suspected_non_patient");
		}

		return flags;
	},
};

const phoneKindLabel: Record<PhoneKind, string> = {
	mobile: "móvel",
	landline: "fixo",
	international: "internacional",
	unknown: "desconhecido",
};

const phoneKindChip: Partial<Record<PhoneKind, string>> = {
	landline: "fixo",
	international: "internacional",
};

export const FICHA_TABS = ["cadastro", "conversa"] as const;

export type FichaTab = (typeof FICHA_TABS)[number];

const fichaTabLabel: Record<FichaTab, string> = {
	cadastro: "Cadastro",
	conversa: "Conversa",
};

export const FICHA_FOCUS = ["financeiro", "clinico", "cadastro"] as const;

export type FichaFocus = (typeof FICHA_FOCUS)[number];

const clinicalKindLabel: Record<"prontuario" | "exames" | "manual" | "documentos", string> = {
	prontuario: "Prontuário",
	exames: "Exames",
	manual: "Manual",
	documentos: "Documentos",
};

const fichaTabPlaceholder: Record<"financeiro" | "prontuario" | "exames" | "manual", string> = {
	financeiro: "Sem dados financeiros para esta pessoa.",
	prontuario: "Sem prontuário para esta pessoa.",
	exames: "Sem exames para esta pessoa.",
	manual: "Sem manuais para esta pessoa.",
};

export const PessoaLabels = {
	phoneKind: phoneKindLabel,
	phoneKindChip,
	fichaTabs: fichaTabLabel,
	clinicalKind: clinicalKindLabel,
	fichaTabPlaceholder,
	unnamedPatient: "Paciente sem nome",
	patient: "Paciente",
	lead: "Lead",
	suspectedNonPatient: "Possível não-paciente",
	registry: {
		heading: "Cadastro",
		identitySection: "Identidade",
		contactSection: "Contato",
		addressSection: "Endereço e preferência",
		contextSection: "Convênio e origem",
		edit: "Editar cadastro",
		name: "Nome civil",
		cpf: "CPF",
		birth: "Nascimento",
		phones: "Telefones",
		email: "E-mail",
		address: "Endereço",
		origin: "Origem",
		since: "No pulso desde",
		principal: "Principal",
		years: (n: number) => `${n} anos`,
		birthSuspect: "data suspeita",
		sinceErp: "registro no ERP",
		sinceWhatsApp: "primeiro contato no WhatsApp",
		birthDiverge: (value: string) => `Outra fonte registrou ${value}.`,
		birthKeep: (value: string) => `Manter ${value}`,
		birthReplace: (value: string) => `Usar ${value}`,
		consentsHeading: "Consentimentos",
		sex: "Sexo",
		maritalStatus: "Estado civil",
		motherName: "Nome da mãe",
		rg: "RG",
		insurance: "Convênio",
		occupation: "Profissão",
		howFound: "Como conheceu",
		preferredChannel: "Canal preferido",
		referredBy: (name: string) => `Indicação de ${name}`,
		clinico: "Clínico",
		clinicoGate: "Acesso restrito ao perfil médico.",
		save: "Salvar alterações",
		saving: "Salvando alterações...",
		saveError: "Não foi possível salvar as alterações.",
		leaveTitle: "Alterações não salvas",
		leaveDescription: "As alterações desta pessoa serão perdidas se você sair.",
		stay: "Continuar editando",
		leave: "Descartar e sair",
	},
	consent: {
		sms_marketing: "Consentiu SMS de marketing",
		wa_marketing: "Consentiu marketing pelo WhatsApp",
		wa_reminder: "Consentiu lembretes pelo WhatsApp",
		wa_birthday: "Consentiu mensagem de aniversário no WhatsApp",
		email_marketing: "Consentiu marketing por e-mail",
		email_reminder: "Consentiu lembretes por e-mail",
		email_birthday: "Consentiu mensagem de aniversário por e-mail",
	},
	consentChip: {
		wa_marketing: "WhatsApp marketing",
		wa_reminder: "WhatsApp lembrete",
		wa_birthday: "WhatsApp aniversário",
		email_marketing: "E-mail marketing",
		email_reminder: "E-mail lembrete",
		email_birthday: "E-mail aniversário",
		sms_marketing: "SMS marketing",
	},
	provenanceFrom: "Veio de",
	side: {
		financeiro: "Financeiro",
		agenda: "Agenda",
		provenance: "Proveniência",
		submissions: "Histórico de envios",
		timeline: "Linha do tempo",
	},
	sex(value: string) {
		return sexLabel[value] ?? value;
	},
	filesHeading: "Arquivos",
	conversationLink: "Abrir conversa ao vivo",
	contactButton: "Abrir conversa",
	headerPill: {
		divergences: (n: number) => (n === 1 ? "1 divergência" : `${n} divergências`),
		conflicts: (n: number) => (n === 1 ? "1 conflito" : `${n} conflitos`),
		completeRegistration: "Completar cadastro",
		financeiro: "Financeiro",
		clinico: "Clínico",
	},
	fieldConflict: {
		trigger: "Ver divergência ou conflito deste campo",
		divergences: "Divergência de cadastro",
		conflicts: "Conflito entre fontes",
		relatedPerson: "Pessoa relacionada",
	},
};

const roleLabel: Record<UserRole, string> = {
	staff: "equipe",
	medical: "médico",
};

const userTypeLabel: Record<"admin" | "user", string> = {
	admin: "admin",
	user: "operador",
};

const authErrorLabel: Record<string, string> = {
	UNAUTHORIZED: "Usuária ou senha inválidos.",
	PASSWORD_CHANGE_REQUIRED: "Troca de senha obrigatória.",
	FORBIDDEN: "Acesso restrito ao perfil médico.",
};

export const AuthLabels = {
	role: roleLabel,
	userType: userTypeLabel,

	loginTitle: "Pulso",
	loginSubtitle: "Acesso da equipe",
	changeTitle: "Defina uma senha nova",
	changeSubtitle: "A senha precisa ter ao menos 8 caracteres.",
	newPassword: "Nova senha",
	confirmPassword: "Confirme a nova senha",
	saveAndEnter: "Salvar e entrar",
	savingPassword: "Salvando senha...",
	signIn: "Entrar",
	signingIn: "Entrando...",
	mismatch: "As senhas não conferem.",
	tooShort: "A senha precisa de ao menos 8 caracteres.",
	restricted: "Acesso restrito ao perfil médico.",

	error(code: string | undefined) {
		if (!code) {
			return authErrorLabel.UNAUTHORIZED;
		}

		return authErrorLabel[code] ?? authErrorLabel.UNAUTHORIZED;
	},
};

const speakerLabel: Record<MessageDirection, string> = {
	contact: "ela:",
	clinic: "você:",
};

const speakerTone: Record<MessageDirection, string> = {
	contact: "text-whatsapp",
	clinic: "text-muted-foreground",
};

export const AgoraLabels = {
	panelTitle: "Respondendo agora",
	panelEmpty: "nenhuma conversa com janela aberta",
	panelError: "Não foi possível carregar as conversas ao vivo.",
	queueCount(total: number) {
		return `${total} ${total === 1 ? "pendência" : "pendências"} na fila`;
	},
	liveCount(total: number) {
		return `${total} ${total === 1 ? "conversa" : "conversas"} ao vivo`;
	},
	eyebrowDate(date: Date) {
		const formatted = new Intl.DateTimeFormat("pt-BR", {
			weekday: "long",
			day: "numeric",
			month: "long",
		}).format(date);

		return formatted.replace("-feira", "");
	},
};

export const AoVivoLabels = {
	window(hoursLeft: number | null) {
		if (hoursLeft === null) {
			return "janela fechada";
		}
		return `${Math.ceil(hoursLeft)}h`;
	},
	speaker: speakerLabel,
	speakerTone,

	summary(text: string | null) {
		if (text === null) {
			return "[nova mensagem]";
		}

		return text;
	},

	wait(minutes: number | null) {
		if (minutes === null) {
			return null;
		}

		if (minutes < 60) {
			return `${minutes}min`;
		}

		const hours = Math.floor(minutes / 60);
		const rest = minutes % 60;

		return `${hours}h${String(rest).padStart(2, "0")}`;
	},

	waitChip(minutes: number) {
		return `espera há ${this.wait(minutes)}`;
	},
};

export const ConversaLabels = {
	title: "Conversa",
	loading: "Carregando conversa...",
	error: "Não foi possível carregar a conversa.",
	empty: "Este contato não tem mensagens.",
	beginning: "Início da conversa",
	newMessages: "novas mensagens",
	work: {
		reading: "Leitura da conversa",
		objections: "Objeções",
		profile: "Perfil",
		communication: "Comunicação",
		reactivation: "Reativação",
	},
	mediaCount(total: number) {
		if (total === 1) {
			return "1 mídia na conversa";
		}

		return `${total} mídias na conversa`;
	},
};

const bubbleSealLabel: Record<"sent" | "simulated", string> = {
	sent: "enviada, aguardando confirmação",
	simulated: "simulada",
};

type FileMediaType = Exclude<FileFilter, "all">;

const fileFilterOptions: { value: FileFilter; label: string }[] = [
	{ value: "all", label: "Todos" },
	{ value: "image", label: "Imagens" },
	{ value: "document", label: "Documentos" },
	{ value: "video", label: "Vídeos" },
];

const fileEmptyLabel: Record<FileFilter, string> = {
	all: "nenhum arquivo nesta conversa",
	image: "nenhuma imagem nesta conversa",
	document: "nenhum documento nesta conversa",
	video: "nenhum vídeo nesta conversa",
};

const fileTypeLabel: Record<FileMediaType, string> = {
	image: "Imagem",
	document: "Documento",
	video: "Vídeo",
};

const fileDirectionLabel: Record<MessageDirection, string> = {
	clinic: "enviado",
	contact: "recebido",
};

export const CockpitLabels = {
	bubbleSeal: bubbleSealLabel,
	files: {
		title: "Arquivos da conversa",
		filters: fileFilterOptions,
		empty: fileEmptyLabel,
		type: fileTypeLabel,
		direction: fileDirectionLabel,
	},
	unnamed: "Contato sem nome",
	civil: {
		patient_confirmed: "paciente confirmado",
		none: "sem cadastro civil",
	},
	chip: {
		noWaMarketing: "sem consentimento de marketing",
		sharedPhone: "telefone compartilhado",
		stage(stage: string) {
			return stage;
		},
	},
	mode(live: boolean, replicaAgeMin: number | null) {
		if (live) {
			return "ao vivo";
		}

		if (replicaAgeMin === null) {
			return "réplica";
		}

		return `réplica atualizada há ${replicaAgeMin}min`;
	},
	window(open: boolean, hoursLeft: number | null) {
		if (!open) {
			return "janela fechada";
		}

		if (hoursLeft === null) {
			return "janela aberta";
		}

		return `janela aberta por cerca de ${Math.ceil(hoursLeft)}h`;
	},
	lastOutbound(author: string | null, minutes: number) {
		return `${author ?? "Pulso"}, há ${minutes}min`;
	},
	composer: {
		suggestionBadge: "sugestão do copiloto",
		directed: "com direção da equipe",
		neutral: "telefone compartilhado",
		preGenerated(hhmm: string) {
			return `pré-gerado às ${hhmm}`;
		},
		autoToggle: "Copiloto automático",
		blockReplica: "copiloto automático indisponível na réplica",
		placeholderOpen: "Resposta",
		placeholderClosed: "Mensagem de continuidade",
		directionPlaceholder: "Direção para o copiloto (opcional)",
		sendReal: "Enviar no WhatsApp",
		sendSimulated: "Simular envio",
		suggestOpen: "Sugerir resposta",
		suggestClosed: "Rascunhar mensagem-chave",
		suggestNew: "Nova sugestão",
		writing: "Escrevendo sugestão...",
		discard: "Descartar",
		offerSlotTrigger: "oferecer horário",
		offerSlotHeader: "horários disponíveis",
		teaser: "Enviar abridor",
		teaserFired: "Resposta ao abridor pendente",
		slotChip(dayLabel: string, time: string) {
			return `${dayLabel} ${time}`;
		},
		slotDirection(dayLabel: string, time: string) {
			return `ofereça ${dayLabel} às ${time} para consulta`;
		},
	},
	productActivity: {
		title: "Atividade do produto",
		lastContact(when: string) {
			return `equipe falou pelo Pulso em ${when}`;
		},
		replica(minutes: number) {
			if (minutes < 90) {
				return `réplica há ${minutes} min`;
			}

			return `réplica há ${Math.round(minutes / 60)} h`;
		},
	},
};

const timelineKindLabel: Record<TimelineKind, string> = {
	action: "Ação",
	send: "Envio",
	teaser: "Abridor",
	reply: "Resposta ao abridor",
	civil: "Marco civil",
	agenda: "Agenda",
	financial: "Financeiro",
	medical: "Médico",
};

const timelineSourceLabel: Record<TimelineSource, string> = {
	team: "equipe",
	product: "produto",
	whatsapp: "whatsapp",
	ai: "IA",
	civil: "civil",
	agenda: "agenda",
	financial: "financeiro",
	medical: "médico",
};

const timelineSourceTone: Record<TimelineSource, string> = {
	team: "border-primary/25 bg-primary/10 text-primary",
	product: "border-warning/30 bg-warning/10 text-warning",
	whatsapp: "border-info/25 bg-info/10 text-info",
	ai: "border-destructive/30 bg-destructive/10 text-destructive",
	civil: "border-border bg-border/40 text-muted-foreground",
	agenda: "border-info/25 bg-info/10 text-info",
	financial: "border-warning/30 bg-warning/10 text-warning",
	medical: "border-destructive/30 bg-destructive/10 text-destructive",
};

const timelineSourceDot: Record<TimelineSource, string> = {
	team: "border-primary",
	product: "border-primary",
	whatsapp: "border-info",
	ai: "border-primary",
	civil: "border-muted-foreground",
	agenda: "border-info",
	financial: "border-warning",
	medical: "border-destructive",
};

const actionStatusLabel = {
	feita: "Ação concluída",
	contestada: "Ação contestada",
	fechada_pelo_agente: "Ação encerrada pela IA",
	substituida: "Pendência encerrada",
	created: "Pendência identificada",
	updated: "Pendência atualizada",
	contact: "Contato registrado",
	waiting: "Pendência aguardando",
	transferred: "Pendência transferida",
	completed: "Pendência concluída",
	contested: "Pendência contestada",
	closed: "Pendência encerrada",
	reopened: "Retorno à fila",
};

const sendChannelLabel = {
	free: "texto livre",
	template: "template",
};

const civilCodeLabel = {
	civil_erp: "Cadastro no ERP",
	civil_whatsapp: "Entrou no WhatsApp da clínica",
};

function actionTitle(code: string) {
	const [, status] = code.split(":");

	if (Object.hasOwn(actionStatusLabel, status)) {
		return actionStatusLabel[status as keyof typeof actionStatusLabel];
	}

	return status;
}

function sendTitle(code: string) {
	const [outcome] = code.split(":");
	if (outcome === "send_blocked") {
		return "Envio bloqueado";
	}

	return "Falha no envio";
}

function teaserTitle(code: string) {
	if (code === "teaser_dry") {
		return "Abridor simulado";
	}

	return "Abridor disparado";
}

function financialTitle(code: string) {
	const [event] = code.split(":");

	return finTimelineLabel[event === "payment_reversed" ? "payment_reversed" : "payment_received"];
}

const agendaPhaseSuffix = {
	scheduled: "agendado",
	done: "realizado",
	missed: "faltou",
};

function agendaTitle(code: string) {
	const [tipo, phase] = code.split(":");

	return `${agendaTipoLabel[tipo as AgendaTipoValue]} ${agendaPhaseSuffix[phase as keyof typeof agendaPhaseSuffix]}`;
}

const medicalEventTitleLabel = {
	...milestoneTypeLabel,
	consult: "Consulta registrada",
	manual: "Manual entregue",
	manual_opened: "Manual aberto pela paciente",
};

function medicalTitle(code: string) {
	if (Object.hasOwn(medicalEventTitleLabel, code)) {
		return medicalEventTitleLabel[code as keyof typeof medicalEventTitleLabel];
	}

	return code;
}

export const TimelineLabels = {
	kind: timelineKindLabel,
	source: timelineSourceLabel,
	sourceTone: timelineSourceTone,
	loadError: "Não foi possível carregar a linha do tempo.",
	emptyTitle: "Nenhum evento registrado",
	evidence: "Evidência",
	byAuthor(author: string) {
		return `por ${author}`;
	},
	metadata(event: TimelineEntry) {
		if (event.kind === "action") {
			const [, , actionType] = event.code.split(":");
			const labels = {
				medica: "responsável: médica",
				secretaria: "responsável: secretária",
				conversation: "conversa",
				person: "cadastro",
				people: "localizar pessoa",
				agenda: "agenda",
				finance: "financeiro",
				clinical: "prontuário",
			};

			return labels[actionType as keyof typeof labels];
		}

		if (event.kind === "send") {
			const [, channel, status] = event.code.split(":");
			let channelLabel = channel;
			if (Object.hasOwn(sendChannelLabel, channel)) {
				channelLabel = sendChannelLabel[channel as keyof typeof sendChannelLabel];
			}

			if (event.code.startsWith("send_blocked:")) {
				return channelLabel;
			}

			return `${channelLabel}, status ${status}`;
		}

		if (event.kind === "teaser" && event.code !== "teaser_dry") {
			const [, state] = event.code.split(":");

			return state;
		}

		if (event.kind === "financial") {
			const [, cents] = event.code.split(":");

			return finBrl.format(Number(cents) / 100);
		}

		return null;
	},

	dotTone(event: TimelineEntry) {
		return timelineSourceDot[event.source];
	},

	title(event: TimelineEntry) {
		switch (event.kind) {
			case "action":
				return actionTitle(event.code);
			case "send":
				return sendTitle(event.code);
			case "teaser":
				return teaserTitle(event.code);
			case "reply":
				return "Respondeu ao abridor";
			case "civil":
				if (Object.hasOwn(civilCodeLabel, event.code)) {
					return civilCodeLabel[event.code as keyof typeof civilCodeLabel];
				}

				return event.code;
			case "agenda":
				return agendaTitle(event.code);
			case "financial":
				return financialTitle(event.code);
			case "medical":
				return medicalTitle(event.code);
		}
	},
};
